import React, { useState } from "react";
import GlobalStyles, { CommonClasses } from "../../global/GlobalStyle";
import { MdEmail, MdLock, MdVisibility, MdVisibilityOff } from "react-icons/md";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";

const Login = () => {
  const MailIcon = MdEmail as React.FC<React.SVGProps<SVGSVGElement>>;
  const LockIcon = MdLock as React.FC<React.SVGProps<SVGSVGElement>>;
  const EyeIcon = MdVisibility as React.FC<React.SVGProps<SVGSVGElement>>;
  const EyeOffIcon = MdVisibilityOff as React.FC<React.SVGProps<SVGSVGElement>>;

  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    login: "",
    password: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.id]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    // More aggressive prevention
    e.preventDefault();
    e.stopPropagation();

    // Prevent multiple submissions
    if (isSubmitting) {
      return false;
    }

    setIsSubmitting(true);

    try {
      console.log("Login form submitted"); // Debug log
      const success = await login(formData);
      console.log("Login result:", success); // Debug log
      if (success) {
        navigate("/dashboard");
      }
    } catch (error) {
      console.error("Login error:", error);
    } finally {
      setIsSubmitting(false);
    }

    return false; // Additional prevention
  };

  const togglePasswordVisibility = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowPassword(!showPassword);
  };

  // Also prevent form submission on Enter key in inputs
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && isSubmitting) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  return (
    <div className={GlobalStyles.containerCenter}>
      <div className={GlobalStyles.formContainer}>
        {/* Header */}
        <div className={GlobalStyles.formHeader}>
          <h2 className={GlobalStyles.formHeaderTitle}>Welcome back</h2>
          <p className={GlobalStyles.formHeaderSubtitle}>
            Please enter your credentials to login.
          </p>
        </div>

        {/* Form Fields */}
        <form
          className="p-5"
          onSubmit={handleSubmit}
          noValidate
          onKeyDown={handleKeyDown}
        >
          {/* Email/Username */}
          <div className={GlobalStyles.formGroup}>
            <label htmlFor="login" className={GlobalStyles.label}>
              Email address or Username
            </label>
            <div className={GlobalStyles.inputWrapper}>
              <MailIcon className={GlobalStyles.inputIcon} />
              <input
                id="login"
                type="text"
                placeholder="Email or Username"
                className={GlobalStyles.input}
                value={formData.login}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                required
                disabled={isSubmitting}
              />
            </div>
          </div>

          {/* Password */}
          <div className={GlobalStyles.formGroup}>
            <label htmlFor="password" className={GlobalStyles.label}>
              Password
            </label>
            <div className={`${GlobalStyles.inputWrapper} relative`}>
              <LockIcon className={GlobalStyles.inputIcon} />
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="Enter password"
                className={GlobalStyles.input}
                value={formData.password}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                required
                disabled={isSubmitting}
              />
              <button
                type="button"
                className="absolute right-3 cursor-pointer bg-transparent border-none focus:outline-none"
                onClick={togglePasswordVisibility}
                disabled={isSubmitting}
              >
                {showPassword ? (
                  <EyeOffIcon className={GlobalStyles.inputIcon} />
                ) : (
                  <EyeIcon className={GlobalStyles.inputIcon} />
                )}
              </button>
            </div>
          </div>

          {/* Forgot Password */}
          <div className="text-right mt-2">
            <Link
              to="/forgot-password"
              className={GlobalStyles.linkAccent}
              onClick={(e) => isSubmitting && e.preventDefault()}
            >
              Forgot password?
            </Link>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className={`${GlobalStyles.buttonPrimary} !w-full mt-5 ${
              isSubmitting ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            {isSubmitting ? "Logging in..." : "Login"}
          </button>

          {/* Signup Link */}
          <div
            className={`mt-6 text-center ${CommonClasses.textXs} ${CommonClasses.textGray}`}
          >
            Don't have an account?{" "}
            <Link
              to="/register"
              className={GlobalStyles.linkAccent}
              onClick={(e) => isSubmitting && e.preventDefault()}
            >
              Sign up
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;
